import { useState } from 'react'
import PropTypes from 'prop-types'
import { CheckCircle, Info, XCircle } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { Tooltip } from '@/components/ui/tooltip'
import { formatDateOnly } from '@/utils/date/format-date-only'

import ConfirmPopup from '../../../Popups/ConfirmPopup'

// Note: mirrors src/styles/base/customizations-elevatedliving.scss ($ctp-color__*). Not imported
// from src/helpers/constants.js — that module reads import.meta.env at load time, which breaks
// under Jest's CommonJS transform.
const ICON_COLORS = {
  primary: '#000000',
  red: '#ce2c21',
  green: '#548671',
  grey: '#646464',
}

const pinBlockStyle = {
  borderTop: '1px solid #ebebeb',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  marginTop: '2vh',
  paddingTop: '2vh',
  width: '100%',
}

const rowStyle = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  justifyContent: 'space-between',
}

const infoGroupStyle = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
}

const IglooCustomPinBlock = ({
  pin,
  canEdit,
  residentName,
  onRevoke,
  onResend,
  isRevoking,
  isResending,
}) => {
  const { t } = useTranslation(['clients'])
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false)

  // Staff never create a custom PIN — the resident does, from their app. With no record there is
  // nothing to show and no action to offer.
  if (pin.status === 'none') return null

  if (pin.status === 'pending') {
    return (
      <div style={pinBlockStyle}>
        <div style={infoGroupStyle}>
          <span style={{ color: ICON_COLORS.grey }}>
            {t('clients:keylessAccess.igloo.customPin.pendingActivation')}
          </span>
          <Tooltip triggerIcon={Info} placement="bottom-end">
            {t('clients:keylessAccess.igloo.customPin.tooltip')}
          </Tooltip>
        </div>
      </div>
    )
  }

  if (pin.status === 'revoked') {
    return (
      <div style={pinBlockStyle}>
        <div style={infoGroupStyle}>
          <span>{t('clients:keylessAccess.igloo.customPin.revoked')}</span>
          <CheckCircle size={18} color={ICON_COLORS.green} />
          <span style={{ color: ICON_COLORS.grey }}>|</span>
          <span>{t('clients:keylessAccess.igloo.customPin.unitLockSyncRequired')}</span>
          <Tooltip triggerIcon={Info} placement="bottom-end">
            {t('clients:keylessAccess.igloo.customPin.syncTooltip')}
          </Tooltip>
        </div>
      </div>
    )
  }

  // status === 'active'
  return (
    <div style={pinBlockStyle}>
      <div style={rowStyle}>
        <div style={infoGroupStyle}>
          <span>
            {pin.activatedAt
              ? t('clients:keylessAccess.igloo.customPin.issuedOn', {
                  date: formatDateOnly(pin.activatedAt),
                })
              : t('clients:keylessAccess.igloo.customPin.issued')}
          </span>
          <span style={{ color: ICON_COLORS.grey }}>|</span>
          <span style={{ color: ICON_COLORS.primary }}>
            {t('clients:keylessAccess.igloo.customPin.emailStatus.label')}
          </span>
          {pin.emailStatus === 'failed' ? (
            <>
              <XCircle size={18} color={ICON_COLORS.red} />
              <span style={{ color: ICON_COLORS.red, fontWeight: 500 }}>
                {t('clients:keylessAccess.igloo.customPin.emailStatus.failed')}
              </span>
            </>
          ) : pin.emailStatus === 'sent' ? (
            <>
              <CheckCircle size={18} color={ICON_COLORS.green} />
              <span style={{ color: ICON_COLORS.green, fontWeight: 500 }}>
                {t('clients:keylessAccess.igloo.customPin.emailStatus.sent')}
              </span>
            </>
          ) : (
            <span style={{ color: ICON_COLORS.grey, fontWeight: 500 }}>
              {t('clients:keylessAccess.igloo.customPin.emailStatus.pending')}
            </span>
          )}
        </div>
        <div style={{ ...infoGroupStyle, flexWrap: 'nowrap' }}>
          {canEdit && (pin.emailStatus === 'failed' || pin.emailStatus === 'pending') && (
            <button
              type="button"
              className={`button button-primary ${isResending ? 'is-loading' : ''}`}
              disabled={isResending}
              onClick={() => onResend()}
            >
              {t('clients:keylessAccess.igloo.customPin.resendEmail')}
            </button>
          )}
          {canEdit && (
            <>
              <button
                type="button"
                className={`button button-primary ${isRevoking ? 'is-loading' : ''}`}
                disabled={isRevoking}
                onClick={() => setShowRevokeConfirm(true)}
              >
                {t('clients:keylessAccess.igloo.customPin.revoke.action')}
              </button>
              <Tooltip triggerIcon={Info} placement="bottom-end">
                {t('clients:keylessAccess.igloo.customPin.tooltip')}
              </Tooltip>
            </>
          )}
        </div>
      </div>
      <ConfirmPopup
        title={t('clients:keylessAccess.igloo.customPin.revoke.confirmTitle')}
        body={t('clients:keylessAccess.igloo.customPin.revoke.confirmBody', {
          residentName:
            residentName || t('clients:keylessAccess.igloo.customPin.revoke.residentFallback'),
        })}
        cancelText={t('clients:keylessAccess.igloo.customPin.revoke.cancelText')}
        confirmText={t('clients:keylessAccess.igloo.customPin.revoke.confirmText')}
        isOpen={showRevokeConfirm}
        isLoading={isRevoking}
        cancelCallback={() => setShowRevokeConfirm(false)}
        confirmCallback={async () => {
          try {
            await onRevoke()
          } finally {
            setShowRevokeConfirm(false)
          }
        }}
      />
    </div>
  )
}

IglooCustomPinBlock.propTypes = {
  pin: PropTypes.shape({
    activatedAt: PropTypes.instanceOf(Date),
    deviceName: PropTypes.string,
    emailStatus: PropTypes.oneOf(['sent', 'failed', 'pending', 'none']),
    status: PropTypes.oneOf(['pending', 'active', 'revoked', 'none']).isRequired,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  residentName: PropTypes.string,
  onRevoke: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  isRevoking: PropTypes.bool,
  isResending: PropTypes.bool,
}

IglooCustomPinBlock.defaultProps = {
  residentName: '',
  isRevoking: false,
  isResending: false,
}

export default IglooCustomPinBlock
